import {
  createMember,
  CreateMemberApiError,
  type CreatedMemberContract,
} from '@urbanik/api-client';
import {
  Button,
  CanonicalPageHeader,
  InfoPanel,
  ConfirmationDialog,
  FormActions,
  FormSection,
  InputField,
} from '@urbanik/ui';
import { useState, type FormEvent } from 'react';
import { apiBase } from './api-base';
import './member-create.css';
type Draft = {
  nationalId: string;
  givenNames: string;
  familyNames: string;
  email: string;
  primaryPhone: string;
  secondaryPhone: string;
};
const empty: Draft = {
  nationalId: '',
  givenNames: '',
  familyNames: '',
  email: '',
  primaryPhone: '',
  secondaryPhone: '',
};
export function CreateMemberView() {
  const returnParams = new URLSearchParams(window.location.hash.split('?')[1] ?? ''),
    returnTo = returnParams.get('return'),
    transferSource = returnParams.get('source'),
    transferAssignment = returnParams.get('assignment');
  const transferReturnHash =
    returnTo === 'transfer' && transferSource ? `#socios/${transferSource}?tab=aliquots` : null;
  const returnHash =
    returnTo === 'conciliacion-socios' ? '#conciliacion-socios' : (transferReturnHash ?? '#socios');
  const [draft, setDraft] = useState(empty);
  const [errors, setErrors] = useState<Partial<Record<keyof Draft, string>>>({});
  const [review, setReview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState('');
  const [created, setCreated] = useState<CreatedMemberContract | null>(null);
  const change = (key: keyof Draft, value: string) => {
    setDraft((v) => ({ ...v, [key]: value }));
    setErrors((v) => ({ ...v, [key]: undefined }));
    setProblem('');
  };
  const validate = () => {
    const next: Partial<Record<keyof Draft, string>> = {};
    if (!/^\d{6,20}$/.test(draft.nationalId.replace(/\D/g, '')))
      next.nationalId = 'Ingresa entre 6 y 20 dígitos.';
    if (!draft.givenNames.trim()) next.givenNames = 'Los nombres son obligatorios.';
    if (!draft.familyNames.trim()) next.familyNames = 'Los apellidos son obligatorios.';
    if (draft.givenNames.trim().length > 100) next.givenNames = 'Máximo 100 caracteres.';
    if (draft.familyNames.trim().length > 100) next.familyNames = 'Máximo 100 caracteres.';
    if (draft.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.email))
      next.email = 'Ingresa un correo válido.';
    if (draft.email.length > 254) next.email = 'Máximo 254 caracteres.';
    if (draft.primaryPhone.length > 25) next.primaryPhone = 'Máximo 25 caracteres.';
    if (draft.secondaryPhone.length > 25) next.secondaryPhone = 'Máximo 25 caracteres.';
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const prepare = (event: FormEvent) => {
    event.preventDefault();
    if (validate()) setReview(true);
  };
  const submit = async () => {
    if (busy) return;
    setBusy(true);
    setProblem('');
    try {
      setCreated((await createMember(apiBase, draft)).member);
      setReview(false);
    } catch (error) {
      if (error instanceof CreateMemberApiError && error.kind === 'unauthorized') {
        window.location.hash = '#login';
        return;
      }
      setProblem(error instanceof Error ? error.message : 'No pudimos registrar al socio.');
      setReview(false);
    } finally {
      setBusy(false);
    }
  };
  if (created)
    return (
      <div className="shell-page member-create-page">
        <section className="member-create-success">
          <p className="kicker">M03.3 · Alta completada</p>
          <h1>Socio registrado.</h1>
          <p>
            {created.familyNames}, {created.givenNames} quedó activo con cédula {created.nationalId}
            .
          </p>
          <Button
            type="button"
            variant={transferReturnHash ? 'primary' : 'secondary'}
            onClick={() => {
              window.location.hash =
                transferReturnHash && transferAssignment
                  ? `${transferReturnHash}&transfer=${encodeURIComponent(transferAssignment)}&target=${encodeURIComponent(created.id)}&targetQuery=${encodeURIComponent(created.nationalId)}`
                  : returnHash;
            }}
          >
            {transferReturnHash
              ? 'Continuar transferencia del cupo'
              : returnTo === 'conciliacion-socios'
                ? 'Volver a conciliación'
                : 'Volver a socios'}
          </Button>
        </section>
      </div>
    );
  return (
    <div className="shell-page member-create-page">
      <CanonicalPageHeader
        title="Nuevo socio"
        description="M03.3 · Alta de socios · Completa los datos, revísalos y confirma el registro."
      />
      <form className="member-create-card" onSubmit={prepare} noValidate>
        <FormSection
          title="Identificación"
          description="Estos datos identifican al socio y son obligatorios."
        >
          <InputField
            label="Cédula"
            value={draft.nationalId}
            onChange={(e) => change('nationalId', e.target.value)}
            error={errors.nationalId}
            required
            inputMode="numeric"
          />
          <InputField
            label="Nombres"
            value={draft.givenNames}
            onChange={(e) => change('givenNames', e.target.value)}
            error={errors.givenNames}
            required
            maxLength={100}
          />
          <InputField
            label="Apellidos"
            value={draft.familyNames}
            onChange={(e) => change('familyNames', e.target.value)}
            error={errors.familyNames}
            required
            maxLength={100}
          />
        </FormSection>
        <FormSection
          title="Contacto"
          description="Información opcional para comunicarse con el socio."
        >
          <InputField
            label="Correo"
            type="email"
            value={draft.email}
            onChange={(e) => change('email', e.target.value)}
            error={errors.email}
            maxLength={254}
          />
          <InputField
            label="Teléfono principal"
            value={draft.primaryPhone}
            onChange={(e) => change('primaryPhone', e.target.value)}
            error={errors.primaryPhone}
            maxLength={25}
          />
          <InputField
            label="Teléfono secundario"
            value={draft.secondaryPhone}
            onChange={(e) => change('secondaryPhone', e.target.value)}
            error={errors.secondaryPhone}
            maxLength={25}
          />
        </FormSection>
        {problem ? (
          <InfoPanel tone="danger" title="No se pudo registrar al socio">
            <span role="alert">{problem}</span>
          </InfoPanel>
        ) : null}
        <FormActions>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              window.location.hash = returnHash;
            }}
          >
            {returnTo === 'conciliacion-socios' ? 'Volver a conciliación' : 'Cancelar'}
          </Button>
          <Button type="submit" variant="primary">
            Revisar alta
          </Button>
        </FormActions>
      </form>
      <ConfirmationDialog
        open={review}
        busy={busy}
        title="Confirmar alta de socio"
        description={`Se registrará a ${draft.familyNames.trim()}, ${draft.givenNames.trim()} con cédula ${draft.nationalId.replace(/\D/g, '')}.`}
        onCancel={() => setReview(false)}
        onConfirm={() => void submit()}
      />
    </div>
  );
}
